import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Header from '@/components/Header';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { FadeIn } from '@/components/marketing/FadeIn';

const Index: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = waitlistEmail.trim();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('waitlist-signup', {
        body: { email },
      });
      if (error) {
        toast.error(error.message || 'Something went wrong.');
        return;
      }
      setSubmitted(true);
      setWaitlistEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <Header />

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 py-24 sm:py-32 text-center">
        <FadeIn delay={0}>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
            SocialScoop
          </div>
        </FadeIn>
        <FadeIn delay={100}>
          <h1 className="text-5xl sm:text-6xl font-semibold tracking-tight leading-[1.05]">
            Your feed. Your rules.
          </h1>
        </FadeIn>
        <FadeIn delay={200}>
          <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            One curated stream from Reddit, Bluesky, YouTube, and X — shaped by your standards,
            not a platform's engagement targets.
          </p>
        </FadeIn>
        <FadeIn delay={300}>
          <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
            <Button size="lg" onClick={scrollToWaitlist} className="px-6">
              Join the Beta
            </Button>
            <Link
              to="/how-it-works"
              className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              See how it works
            </Link>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Start for $1. Scales with what you use. No credit card required to try.
          </p>
        </FadeIn>
      </section>

      {/* The Problem */}
      <section className="border-t border-border/60 bg-muted/30">
        <div className="max-w-3xl mx-auto px-6 py-24">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
              The feed you have isn't the one you'd choose.
            </h2>
          </FadeIn>
          <FadeIn delay={100}>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Every platform is engineered to maximize time on screen — not to surface what
              matters to you. The outrage, the ads, the accounts you followed once and can't
              escape. The content designed to provoke a reaction rather than inform a thought.
              You didn't design that feed. They did. SocialScoop gives it back to you —
              permanently.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* How It Works */}
      <section className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight max-w-2xl mb-3">
              Three steps to a feed worth opening.
            </h2>
            <p className="text-base text-muted-foreground mb-12">No algorithms optimizing for your outrage. No black box. Just your standards, applied.</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <article className="rounded-xl border border-border bg-card p-7 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 h-full">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                Step 1
              </div>
              <h3 className="text-lg font-semibold tracking-tight">Connect your platforms</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Link Reddit, Bluesky, YouTube, and X in seconds via secure OAuth. Your passwords
                never touch our servers — only the tokens needed to fetch your content.
              </p>
            </article>
            <article className="rounded-xl border border-border bg-card p-7 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 h-full">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                Step 2
              </div>
              <h3 className="text-lg font-semibold tracking-tight">Set your standards</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Listening Rules surface what you care about. Fences remove what you don't.
                Plain English, no Boolean logic. Your onboarding quiz builds your first set
                automatically — you refine from there.
              </p>
            </article>
            <article className="rounded-xl border border-border bg-card p-7 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 h-full">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                Step 3
              </div>
              <h3 className="text-lg font-semibold tracking-tight">Open to a feed that's done its job</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                SocialScoop fetches and filters in the background every 20 minutes. When you
                arrive, everything is already curated, scored by AI, and sorted by relevance.
                You read. You don't scroll.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="border-t border-border/60 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <FadeIn>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-10">Who it's for</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            <div>
              <div className="text-sm font-semibold tracking-tight mb-2">
                The reader, not the reactor.
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You want to stay sharp without being pulled under. You read before you share.
                You want a morning routine that leaves you better informed, not more agitated.
                SocialScoop is built for you.
              </p>
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight mb-2">
                The parent who sets the standard.
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                A feed you'd actually hand your kids. Content fences they can't override, a PIN-protected Vault for everything filtered out, and rules you control — not the platform.
              </p>
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight mb-2">
                The professional who monitors without being consumed.
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Track your industry, your brand, your audience — across Reddit, X, Bluesky and
                YouTube — without surrendering your focus to the feeds designed to feed on it.
              </p>
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight mb-2">
                The academic building in public.
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Curate the discourse in your field, add your own papers and articles, and
                share your expert-filtered feed with followers. Your curation is the contribution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rules Engine */}
      <section className="border-t border-border/60">
        <div className="max-w-3xl mx-auto px-6 py-24">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            Not a black box. A philosophy.
          </h2>
          <p className="mt-3 text-base text-muted-foreground font-medium">
            You define the standards. We apply them exactly.
          </p>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Most platforms filter your feed to maximize engagement — which means surfacing content
            designed to provoke a reaction. SocialScoop inverts that entirely. You write the logic.
            You set the intensity. Our AI scores content for sentiment and toxicity, but the final
            call is always yours. Nothing is deleted without your knowledge. Nothing is hidden
            permanently. This is your feed — and it behaves like it.
          </p>
          <ul className="mt-8 space-y-3 text-base">
            <li className="flex gap-3">
              <span className="text-muted-foreground">·</span>
              <span><strong className="font-medium">Listening Rules:</strong> surface more of what you care about, written in plain English</span>
            </li>
            <li className="flex gap-3">
              <span className="text-muted-foreground">·</span>
              <span><strong className="font-medium">Fences (Digital Fencing™):</strong> de-emphasize or remove what drains you — without deleting it</span>
            </li>
            <li className="flex gap-3">
              <span className="text-muted-foreground">·</span>
              <span><strong className="font-medium">Intensity Gauge:</strong> a 0–5 slider for every rule — tuning, not toggling</span>
            </li>
            <li className="flex gap-3">
              <span className="text-muted-foreground">·</span>
              <span><strong className="font-medium">Content Vault:</strong> everything fenced is stored privately, PIN-protected, reviewable any time</span>
            </li>
            <li className="flex gap-3">
              <span className="text-muted-foreground">·</span>
              <span><strong className="font-medium">AI scoring:</strong> Claude-powered sentiment + toxicity analysis — advisory, never overriding</span>
            </li>
            <li className="flex gap-3">
              <span className="text-muted-foreground">·</span>
              <span><strong className="font-medium">Curator feeds:</strong> share your curated feed publicly, build a following, earn from it</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Waitlist CTA */}
      <section id="waitlist" className="border-t border-border/60 bg-muted/30">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            Fifty founding spots. This is one of them.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            We're inviting 50 people to shape SocialScoop from the ground up. Founding Members get
            permanent status, early access to every feature, and a discounted annual rate — but more
            than that, they get a feed that's finally theirs. We're looking for readers, not reactors.
            Professionals who monitor without getting consumed. Parents who set the standard. Academics
            building their audience in public. If you've been waiting for something built around your
            judgment — you just found it.
          </p>
          {submitted ? (
            <div className="mt-10 inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-3 text-sm shadow-sm">
              You're on the list. We'll be in touch.
            </div>
          ) : (
            <form
              onSubmit={handleWaitlistSubmit}
              className="mt-10 flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
            >
              <Input
                type="email"
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="flex-1"
              />
              <Button type="submit" disabled={submitting} className="sm:w-auto">
                {submitting ? 'Sending…' : 'Apply for Founding Access'}
              </Button>
            </form>
          )}
          <p className="mt-4 text-xs text-muted-foreground">
            50 spots total. No spam, ever. Unsubscribe anytime.
          </p>
        </div>
      </section>

      <footer className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>
            <div>Your feed. Your standards. Your attention — finally yours.</div>
            <div className="mt-1">© 2026 SocialScoop.ai — Built for people, not platforms.</div>
          </div>
          <nav className="flex flex-wrap gap-5">
            <Link to="/features" className="hover:text-foreground transition-colors">Features</Link>
            <Link to="/how-it-works" className="hover:text-foreground transition-colors">How It Works</Link>
            <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/data-deletion" className="hover:text-foreground transition-colors">Data Deletion</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Index;
